import React, { useEffect, useRef, useState } from 'react'
import { Select, Spin, Tag } from 'antd'
import { EyeOutlined, SortAscendingOutlined } from '@ant-design/icons'

import { getCapacityText, arrayMove, getShopCountryHeaders } from '../utils'
import { isProd } from '../consts'

import './product-selector.css'

/**
 * This component is to render a searcher with product suggestions,
 * and select some products with this tool.
 */
export function ProductSelector({
  defaultProductInfo,
  disabled,
  disabledIds = new Set([]),
  onChange,
  onChangeProductInfo = () => {},
  onSearch,
  placeholder = 'Search products to add',
  prodSugggestions,
  productTypes,
  width = '100%',
  style = {},
  closable = true,
  products = [],
  state,
  type = 'group',
}) {
  const DEBOUNCE_TIME = 320
  const debounceTimeout = useRef()
  const [productInfo, setProductInfo] = useState({})
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const subdomain = isProd() ? 'www' : 'stage'

  function getProdId(product) {
    return type === 'refs' ? product.ref : product.id
  }

  function getName(pid) {
    return productInfo[pid]?.name ? `${productInfo[pid].name} (${pid})` : pid
  }

  function onSearchProduct(text) {
    setLoading(true)
    if (onSearch) return onSearch(text)

    clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      fetchProductSuggestions({ text, type, productTypes, state })
        .then(setSuggestions)
        .then(() => setLoading(false))
    }, DEBOUNCE_TIME)
  }

  useEffect(() => {
    if (!Array.isArray(prodSugggestions)) return
    setSuggestions(prodSugggestions)
    setLoading(false)
  }, [prodSugggestions])

  useEffect(() => {
    if (type !== 'refs') return
    if (defaultProductInfo) return setProductInfo(defaultProductInfo)

    // ⚠️ This prevents doing an expensive open search
    if (!products || products.length === 0) return

    fetch(`/db/guillotina/@search-product`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        referencias: products,
        serializer: 'summary-all-opts',
      }),
    })
      .then((r) => r.json())
      .then(({ search }) => {
        const pinfo = search.reduce((o, c, index) => {
          const op =
            c.opciones.find((p) => p.referencia === products[index]) || {}
          const vintage = op.anyada ? ` ${op.anyada}` : ''
          const capacity = op.capacidad
            ? ` ${getCapacityText(op.capacidad)}`
            : ''

          o[op.referencia] = {
            name: c.nombre + vintage + capacity,
            url: `${c.id}/#${op.id}`,
          }

          return o
        }, {})
        setProductInfo(pinfo)
        onChangeProductInfo(pinfo)
      })
      .catch((e) => console.error(e))
    return () => clearTimeout(debounceTimeout.current)
  }, [])

  function renderTag(object) {
    const { value, label, onClose } = object

    return (
      <Tag closable={closable} onClose={onClose}>
        {getName(label)}
        <EyeOutlined
          title={`Open a new tab to view ${label} product on vinissimus website`}
          onClick={() =>
            window.open(
              `https://${subdomain}.vinissimus.com/es/vino/${
                productInfo[value]?.url || value
              }/`,
              '_blank'
            )
          }
          style={{
            margin: '0 5px',
            color: '#1890ff',
            fontSize: 14,
            cursor: 'pointer',
          }}
        />
        <SortAscendingOutlined
          onClick={() => {
            const index = products.indexOf(value)
            const prompt = window.prompt('Change product index', `${index}`)
            const newIndex = parseInt(prompt)

            if (prompt === undefined || isNaN(newIndex)) return

            onChange(arrayMove(products, index, newIndex))
          }}
        />
      </Tag>
    )
  }

  return (
    <Select
      mode="multiple"
      size="medium"
      disabled={disabled}
      filterOption={false}
      value={products}
      allowClear={closable}
      tagRender={renderTag}
      notFoundContent={loading ? <Spin size="small" /> : null}
      placeholder={placeholder}
      onSearch={onSearchProduct}
      style={{ width, ...style }}
      onChange={(ids) => {
        onChange(ids, suggestions)
        if (type !== 'refs') return

        setProductInfo((n) => {
          const pinfo = ids
            .map((id) => suggestions.find((s) => getProdId(s) === id))
            .filter((v) => v)
            .reduce(
              (o, c) => {
                o[c.id] = { name: c.nombre, url: `${c.id}/#${c.option.id}` }
                return o
              },
              { ...n }
            )
          onChangeProductInfo(pinfo)
          return pinfo
        })
      }}
    >
      {suggestions.map((s) => {
        const prodId = getProdId(s)
        const isAvanzada = Boolean(s.option?.caracteristicas?.avanzada)
        return (
          <Select.Option
            disabled={disabledIds.has(prodId)}
            key={s.id + s.ref}
            value={prodId}
          >
            {s.nombre} {`(${prodId})`} {isAvanzada && ' (PR)'}
          </Select.Option>
        )
      })}
    </Select>
  )
}

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  ...getShopCountryHeaders(),
}

async function fetchProductSuggestions({ text, type, state, productTypes }) {
  // ⚠️ This prevents doing an expensive open search when the input text is empty
  if (!text) return []

  const size = 15
  const res = await fetch(`/db/guillotina/@search-product`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      // ⚠️ 'summary-all-opts' is a lot more expensive than summary. Only use it when necessary
      serializer: type === 'group' ? 'summary' : 'summary-all-opts',
      nombre: text,
      cursor: 0,
      size,
      tipo: productTypes,
      opciones: {
        estado: state || ['selling', 'notavailable'],
      },
    }),
  })

  if (res.status >= 300) return []

  const { search } = await res.json()

  if (!Array.isArray(search)) return []

  if (type === 'group') {
    return search.map((p) => ({
      id: p.id,
      nombre: p.nombre,
      ref: p.opciones?.[0]?.referencia,
    }))
  }

  return search
    .reduce(
      (all, p) => [
        ...all,
        ...p.opciones.map((o) => {
          const vintage = o.anyada ? ` ${o.anyada}` : ''
          const capacity = o.capacidad ? ` ${getCapacityText(o.capacidad)}` : ''

          return {
            id: p.id,
            nombre: p.nombre + vintage + capacity,
            ref: o.referencia,
            option: o,
          }
        }),
      ],
      []
    )
    .slice(0, size)
}
